import React from 'react';
import { motion } from 'framer-motion';
import { Activity, Heart, Baby, Users, ShieldCheck, Phone } from 'lucide-react';
import ServiceCard from '../components/ServiceCard';
import { servicesData } from '../data/services';
import { GlassCard } from '../components/GlassCard';

const Services = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';

  const stats = [
    {
      icon: <Users size={24} />,
      label: isAr ? 'مقدمي رعاية متخصصين' : 'Specialized Caregivers',
      value: '50+',
    },
    {
      icon: <Heart size={24} />,
      label: isAr ? 'عائلات سعيدة' : 'Happy Families',
      value: '1000+',
    },
    {
      icon: <ShieldCheck size={24} />,
      label: isAr ? 'خدمة مرخصة' : 'Licensed Service',
      value: '100%',
    },
  ];

  return (
    <div
      className='pt-24 pb-20 bg-[#050505]' // Changed from bg-medical-blue-50/30
      dir={isAr ? 'rtl' : 'ltr'}
    >
      {/* 1. Header & Intro */}
      <section className='section-padding py-16'>
        <div className='max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center'>
          <motion.div
            initial={{ opacity: 0, x: isAr ? 50 : -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className='inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-medical-blue-400 text-xs font-black uppercase tracking-widest mb-6'>
              <Activity size={16} />
              {isAr ? 'حلول رعاية شاملة' : 'Comprehensive Care Solutions'}
            </div>
            <h1 className='text-4xl md:text-6xl font-black text-white mb-6'>
              {' '}
              {/* Changed to text-white */}
              {isAr ? 'خدماتنا المتخصصة' : 'Our Professional Care Services'}
            </h1>
            <p className='text-lg text-gray-400 mb-8 leading-relaxed font-medium'>
              {' '}
              {/* Changed to text-gray-400 */}
              {isAr
                ? 'نقدم مجموعة واسعة من خدمات الرعاية الصحية المنزلية المصممة لتلبية الاحتياجات الفريدة لكل مريض وعائلة في الكويت، مع الالتزام بأعلى معايير الجودة الطبية.'
                : 'We provide a wide range of home healthcare services designed to meet the unique needs of every patient and family in Kuwait, adhering to the highest standards of medical quality.'}
            </p>
          </motion.div>

          {/* Stats Bar Component */}
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
            {stats.map((stat, i) => (
              <GlassCard
                key={i}
                className='p-6 text-center border-white/10' // Subtler border for black bg
                delay={i * 0.1}
              >
                <div className='flex justify-center mb-3 text-medical-blue-500'>
                  {stat.icon}
                </div>
                <div className='text-2xl font-black text-white'>
                  {' '}
                  {/* Changed to text-white */}
                  {stat.value}
                </div>
                <div className='text-[10px] font-bold text-gray-500 uppercase tracking-tighter'>
                  {stat.label}
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      {/* 2. Services Grid */}
      <section className='section-padding max-w-7xl mx-auto pb-24'>
        <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-10'>
          {servicesData.map((service, index) => (
            <ServiceCard
              key={service.id}
              lang={lang}
              title={service.title[lang]}
              description={service.description[lang]}
              features={service.features[lang]}
              image={service.image}
              delay={index * 0.1}
              isDark={true} // Enable dark mode styles in ServiceCard
            />
          ))}
        </div>
      </section>

      {/* 3. Personalized Care CTA */}
      <section className='section-padding'>
        <GlassCard className='max-w-5xl mx-auto p-12 bg-medical-blue-600/10 border border-medical-blue-500/20 relative overflow-hidden'>
          {/* Subtle glow effect for CTA */}
          <div className='absolute -inset-24 bg-medical-blue-600/10 blur-[100px] pointer-events-none' />

          <Heart
            size={300}
            className='absolute -bottom-20 -right-20 text-medical-blue-500/10 rotate-12'
          />

          <div className='relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8'>
            <div className={isAr ? 'text-right' : 'text-left'}>
              <h2 className='text-3xl md:text-4xl font-black text-white mb-4'>
                {isAr
                  ? 'هل تحتاج إلى خطة رعاية مخصصة؟'
                  : 'Need a Customized Care Plan?'}
              </h2>
              <p className='text-gray-400 font-medium text-lg max-w-xl'>
                {isAr
                  ? 'يمكننا تصميم باقة خدمات تناسب احتياجاتكم الصحية المحددة وجدولكم الزمني.'
                  : 'We can design a service package that fits your specific health needs and schedule.'}
              </p>
            </div>

            <a
              href='tel:+96550097296'
              className='px-10 py-5 bg-medical-blue-600 text-white rounded-2xl font-black text-lg shadow-xl shadow-medical-blue-600/20 hover:bg-medical-blue-500 transition-all flex items-center gap-3 border border-medical-blue-400/20'
            >
              <Phone size={20} />
              {isAr ? 'تحدث مع مستشار' : 'Speak with a Consultant'}
            </a>
          </div>
        </GlassCard>
      </section>
    </div>
  );
};

export default Services;
